import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Mail, Phone, MapPin, ArrowUpRight, Loader2, Check } from "lucide-react";

import PageMeta from "../components/layout/PageMeta";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";

const SERVICES = [
    "AI Solutions",
    "Talent Solutions",
    "Product Engineering",
    "Product Accelerators",
    "Industry Consulting",
    "Other",
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        phone: "",
        service_interest: "",
        message: "",
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const update = (key, value) => {
        setForm((f) => ({ ...f, [key]: value }));
        setErrors((e) => ({ ...e, [key]: undefined }));
    };

    const validate = () => {
        const next = {};
        if (!form.name.trim()) next.name = "Please tell us your name.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
            next.email = "Enter a valid work email.";
        if (!form.service_interest) next.service_interest = "Pick a service.";
        if (!form.message.trim() || form.message.trim().length < 12)
            next.message = "Add a short note (12+ chars).";
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            toast.error("Please fix the highlighted fields.");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/contact`, form);
            setSubmitted(true);
            toast.success("Thanks — we'll get back to you within one business day.");
            setForm({
                name: "",
                email: "",
                company: "",
                phone: "",
                service_interest: "",
                message: "",
            });
        } catch (err) {
            console.error(err);
            toast.error("Couldn't send your message. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="pqx-page-enter">
            <PageMeta
                title="Contact"
                description="Talk to the Perceptiqx team about AI services, talent solutions or product engineering — we respond within one business day."
            />

            {/* Hero */}
            <section
                className="relative pt-28 md:pt-36 pb-12 md:pb-16 overflow-hidden pqx-dark-panel"
                data-testid="contact-hero"
            >
                <div className="absolute inset-0 pqx-grid-bg-dark opacity-50" />
                <div
                    className="absolute right-[-10%] top-[20%] w-[420px] h-[420px] rounded-full pointer-events-none blur-3xl opacity-50"
                    style={{ background: "radial-gradient(circle, rgba(255,64,64,0.20), transparent 60%)" }}
                />

                <div className="pqx-container relative z-10 max-w-4xl">
                    <span className="pqx-eyebrow !text-[#FF4040]">Contact</span>
                    <h1 className="pqx-h1 mt-5 text-white">
                        Let's build your next{" "}
                        <span className="text-[#FF4040]">AI-powered solution.</span>
                    </h1>
                    <p className="mt-6 text-white/70 text-base md:text-lg lg:text-xl leading-relaxed max-w-2xl">
                        Tell us about your problem, your team or your roadmap. We respond
                        within one business day — with the right person to continue the
                        conversation.
                    </p>
                </div>
            </section>

            {/* Form + contact details */}
            <section
                className="relative bg-[#FAFAF8] pt-20 pb-24"
                data-testid="contact-form-section"
            >
                <div className="pqx-container grid lg:grid-cols-[1.4fr_1fr] gap-10">
                    {/* Form */}
                    <div className="rounded-3xl bg-white border border-[#303030]/8 p-6 md:p-12 shadow-[0_30px_70px_-40px_rgba(48,48,48,0.18)]">
                        {submitted ? (
                            <div
                                className="py-12 text-center"
                                data-testid="contact-success"
                            >
                                <span className="w-14 h-14 grid place-items-center rounded-2xl bg-[#FF4040]/12 text-[#FF4040] mx-auto">
                                    <Check className="w-6 h-6" />
                                </span>
                                <h2 className="pqx-display text-3xl text-[#303030] mt-6">
                                    Message received.
                                </h2>
                                <p className="mt-3 text-[#303030]/70 max-w-md mx-auto">
                                    Thanks for reaching out. A Perceptiqx team member will get
                                    back to you within one business day.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setSubmitted(false)}
                                    className="mt-7 pqx-btn-ghost"
                                    data-testid="contact-send-another"
                                >
                                    Send another message
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={onSubmit} data-testid="contact-form" noValidate>
                                <div className="grid sm:grid-cols-2 gap-5">
                                    <Field
                                        label="Full name"
                                        error={errors.name}
                                        required
                                    >
                                        <Input
                                            data-testid="contact-input-name"
                                            value={form.name}
                                            onChange={(e) => update("name", e.target.value)}
                                            placeholder="Jane Doe"
                                            className="h-11"
                                        />
                                    </Field>
                                    <Field
                                        label="Work email"
                                        error={errors.email}
                                        required
                                    >
                                        <Input
                                            data-testid="contact-input-email"
                                            type="email"
                                            value={form.email}
                                            onChange={(e) => update("email", e.target.value)}
                                            placeholder="jane@company.com"
                                            className="h-11"
                                        />
                                    </Field>
                                    <Field label="Company" error={errors.company}>
                                        <Input
                                            data-testid="contact-input-company"
                                            value={form.company}
                                            onChange={(e) => update("company", e.target.value)}
                                            placeholder="Company name"
                                            className="h-11"
                                        />
                                    </Field>
                                    <Field label="Phone" error={errors.phone}>
                                        <Input
                                            data-testid="contact-input-phone"
                                            value={form.phone}
                                            onChange={(e) => update("phone", e.target.value)}
                                            placeholder="+1 555 000 0000"
                                            className="h-11"
                                        />
                                    </Field>
                                    <Field
                                        label="Service interest"
                                        error={errors.service_interest}
                                        required
                                        className="sm:col-span-2"
                                    >
                                        <Select
                                            value={form.service_interest}
                                            onValueChange={(v) =>
                                                update("service_interest", v)
                                            }
                                        >
                                            <SelectTrigger
                                                data-testid="contact-input-service"
                                                className="h-11"
                                            >
                                                <SelectValue placeholder="Select a service" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {SERVICES.map((s) => (
                                                    <SelectItem
                                                        key={s}
                                                        value={s}
                                                        data-testid={`contact-service-option-${s
                                                            .toLowerCase()
                                                            .replace(/\s+/g, "-")}`}
                                                    >
                                                        {s}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    <Field
                                        label="Your message"
                                        error={errors.message}
                                        required
                                        className="sm:col-span-2"
                                    >
                                        <Textarea
                                            data-testid="contact-input-message"
                                            value={form.message}
                                            onChange={(e) => update("message", e.target.value)}
                                            placeholder="Tell us about your problem, timeline or hiring need…"
                                            rows={6}
                                            className="resize-none"
                                        />
                                    </Field>
                                </div>

                                <button
                                    type="submit"
                                    data-testid="contact-submit"
                                    disabled={loading}
                                    className="mt-7 pqx-btn-accent w-full sm:w-auto disabled:opacity-70 disabled:cursor-not-allowed"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Sending…
                                        </>
                                    ) : (
                                        <>
                                            Send message · we reply in 24h
                                            <ArrowUpRight className="w-4 h-4" />
                                        </>
                                    )}
                                </button>

                                <p className="mt-4 text-xs text-[#303030]/55">
                                    By submitting this form you agree to be contacted by Perceptiqx
                                    regarding your enquiry.
                                </p>
                            </form>
                        )}
                    </div>

                    {/* Aside */}
                    <aside className="space-y-5">
                        <ContactCard
                            icon={<Mail className="w-4 h-4" />}
                            label="Email"
                            value="contact@perceptiqx.com"
                            href="mailto:contact@perceptiqx.com"
                            testid="contact-detail-email"
                        />
                        <ContactCard
                            icon={<MapPin className="w-4 h-4" />}
                            label="Headquarters"
                            value="Kavuri Hills, Hyderabad, India"
                            testid="contact-detail-address"
                        />
                        <ContactCard
                            icon={<Phone className="w-4 h-4" />}
                            label="Phone"
                            value="Available on request"
                            testid="contact-detail-phone"
                        />

                        <div className="rounded-2xl bg-[#191919] text-[#FAFAF8] p-7 relative overflow-hidden">
                            <div className="absolute inset-0 pqx-grid-bg-dark opacity-30" />
                            <div className="relative">
                                <p className="text-xs uppercase tracking-[0.2em] text-white/55">
                                    What happens next
                                </p>
                                <ul className="mt-5 space-y-3 text-[14px] text-white/85">
                                    <li className="flex gap-3">
                                        <span className="pqx-display text-[#FF4040] w-6">01</span>
                                        We review your enquiry and route it to the right team lead.
                                    </li>
                                    <li className="flex gap-3">
                                        <span className="pqx-display text-[#FF4040] w-6">02</span>
                                        You receive a response within one business day.
                                    </li>
                                    <li className="flex gap-3">
                                        <span className="pqx-display text-[#FF4040] w-6">03</span>
                                        We schedule a 30-minute discovery call — no commitment.
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </aside>
                </div>
            </section>
        </div>
    );
}

function Field({ label, error, required, children, className = "" }) {
    return (
        <div className={className}>
            <Label className="text-[12px] uppercase tracking-[0.16em] text-[#303030]/65 font-semibold">
                {label}
                {required && <span className="text-[#FF4040] ml-1">*</span>}
            </Label>
            <div className="mt-2">{children}</div>
            {error && (
                <p className="mt-1.5 text-xs text-[#FF4040]" data-testid="field-error">
                    {error}
                </p>
            )}
        </div>
    );
}

function ContactCard({ icon, label, value, href, testid }) {
    const Wrapper = href ? "a" : "div";
    const props = href ? { href } : {};
    return (
        <Wrapper
            {...props}
            data-testid={testid}
            className="flex items-start gap-4 rounded-2xl bg-white border border-[#303030]/8 p-5 hover:border-[#FF4040]/30 transition-colors"
        >
            <span className="w-10 h-10 grid place-items-center rounded-xl bg-[#FF4040]/12 text-[#FF4040] shrink-0">
                {icon}
            </span>
            <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#303030]/55">
                    {label}
                </p>
                <p className="mt-1 text-[15px] font-semibold text-[#303030]">
                    {value}
                </p>
            </div>
        </Wrapper>
    );
}
